import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import { Box, FlagIcon, Icon, Link, ListGrid, Text, IndustriesIcon, MotivationsIcon } from '@maui/elements'
import { Details, Term, Definition } from '@/components/common/Details'
import Freemium from '@/components/common/Freemium/Freemium'
import { closeModal, openModal } from '@/events'
import { normalizeCampaign, normalizeLinearLine } from '@/api/normalizers'
import AttributionScopedItem from '@/components/common/attributionScope/AttributionScopedItem'
import { CampaignTimeline } from './CampaignTimeline'
import { AttributionScopeProvider } from '@/hooks/useAttributionScope'

export function CampaignDetails({ id, data, fromModal = false }) {
  const campaign = useMemo(() => {
    if (!data) return null
    return normalizeCampaign(data)
  }, [data])

  const {
    actors,
    cve,
    description,
    campaigns,
    earliestReference,
    industries,
    lastReference,
    malware,
    motivations,
    reportedAssociations,
    sourceLocations,
    targetLocations,
    timeline,
    tools,
    vulnerabilities,
  } = campaign

  const formattedDate = (date) => {
    if (!date) return
    const dateObj = new Date(date)
    return format(dateObj, 'MMMM d, yyyy')
  }

  const formattedTimeline = useMemo(() => {
    if (!timeline || !timeline.data.length) return
    return normalizeLinearLine(timeline)
  }, [timeline])

  const onActorClick = (actor) => {
    closeModal()
    openModal('ActorModal', actor.id, actor.name)
  }

  const onMalwareClick = (malware) => {
    closeModal()
    openModal('MalwareModal', malware.id, malware.name)
  }

  const onCveClick = (cve) => {
    closeModal()
    openModal('VulnerabilityModal', cve.id, cve.cve_id)
  }

  const onCampaignClick = (campaign) => {
    closeModal()
    openModal('CampaignModal', campaign.id, campaign.name)
  }

  const typeMap = {
    actor: {
      clickHandler: onActorClick,
      icon: <Icon name='user-secret' />,
    },
    campaign: {
      clickHandler: onCampaignClick,
      icon: <Icon name='bullseye' />,
    },
    malware: {
      clickHandler: onMalwareClick,
      icon: <Icon name='bug' />,
    }
  }

  return (
    <AttributionScopeProvider>
      <Details>
        <Term>Description</Term>
        <Definition>
          {description}
        </Definition>
        {!!formattedTimeline?.length &&
          <>
            <Term my='35px'>Campaign Timeline</Term>
            <Definition marginTop='15px' marginBottom='35px' display="inline-grid">
              {formattedTimeline && Array.isArray(formattedTimeline) &&
                <CampaignTimeline formattedTimeline={formattedTimeline} fromModal={fromModal}/>
              }
              {formattedTimeline === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }
        <Term>Latest Activity</Term>
        <Definition>{ formattedDate(lastReference) }</Definition>

        <Term>Earliest Activity</Term>
        <Definition>{ formattedDate(earliestReference) }</Definition>

        {!!campaigns?.length &&
          <>
            <Term>Associated Campaigns</Term>
            <Definition>
              {campaigns && Array.isArray(campaigns) &&
                <ListGrid list={campaigns} cellWidth={['100%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <Link display='flex' gap='8px' onClick={() => onCampaignClick(item)}>
                        <Icon name='bullseye' />
                        {item.name}
                      </Link>
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {campaigns === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!reportedAssociations?.length &&
          <>
            <Term>Reported Associations</Term>
            <Definition>
              {reportedAssociations && Array.isArray(reportedAssociations) &&
                <ListGrid list={reportedAssociations} cellWidth={['100%', '50%', '33.3333%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <Box display='flex' gap='8px'>
                        { typeMap[item.type].icon }
                        {item.name}
                      </Box>
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {reportedAssociations === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!actors?.length &&
          <>
            <Term>Associated Actors</Term>
            <Definition>
              {actors && Array.isArray(actors) &&
                <ListGrid list={actors} cellWidth={['100%', '50%', '25%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <Link display='flex' gap='8px' onClick={() => onActorClick(item)}>
                        <Icon name='user-secret' />
                        {item.name}
                      </Link>
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {actors === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!industries?.length &&
          <>
            <Term>Targeted Industries</Term>
            <Definition>
              {industries && Array.isArray(industries) &&
                <ListGrid list={industries} cellWidth={['100%', '50%', '33.3333%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <IndustriesIcon
                        name={item.name === 'Governments' ? 'Government' : item.name}
                      />
                      {item.name}
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {industries === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!motivations?.length &&
          <>
            <Term>Motivations</Term>
            <Definition>
              {motivations && Array.isArray(motivations) &&
                <ListGrid list={motivations}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <MotivationsIcon
                        name={item.name}
                      />
                      {item.name}
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {motivations === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!malware?.length &&
          <>
            <Term>Associated Malware</Term>
            <Definition>
              {malware && Array.isArray(malware) &&
                <ListGrid list={malware} cellWidth={['100%', '50%', '25%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <Link display='flex' gap='8px' onClick={() => onMalwareClick(item)}>
                        <Icon name='bug' />
                        {item.name}
                      </Link>
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {malware === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!tools?.length &&
          <>
            <Term>Associated Tools</Term>
            <Definition>
              {tools && Array.isArray(tools) &&
                <ListGrid list={tools} cellWidth={['100%', '50%', '25%']}>
                  {item => (
                    <>
                      <AttributionScopedItem itemScope={item?.attribution_scope}>
                        <Link display='flex' gap='8px' onClick={() => onMalwareClick(item)}>
                          <Icon name='tools' />
                          {item.name}
                        </Link>
                      </AttributionScopedItem>
                    </>
                  )}
                </ListGrid>
              }
              {tools === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!vulnerabilities?.length &&
          <>
            <Term>Associated Vulnerabilities</Term>
            <Definition>
              {vulnerabilities && Array.isArray(vulnerabilities) &&
                <ListGrid list={vulnerabilities} cellWidth={['100%', '50%', '25%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <Link display='flex' gap='8px' onClick={() => onCveClick(item)}>
                        <Icon name="shield-alert" />
                        <Text variant='body1' textOverflow='ellipsis' whiteSpace='nowrap' overflow='hidden'>{item.cve_id}</Text>
                      </Link>
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {cve === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!sourceLocations?.length &&
          <>
            <Term>Source Regions</Term>
            <Definition>
              {sourceLocations && Array.isArray(sourceLocations) &&
                <ListGrid list={sourceLocations} cellWidth={['100%', '50%', '25%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <FlagIcon code={item.iso2} fallback={true} style={{ height: '20px', width: '22px' }} />
                      {item.name}
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {sourceLocations === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

        {!!targetLocations?.length &&
          <>
            <Term>Targeted Regions</Term>
            <Definition>
              {targetLocations && Array.isArray(targetLocations) &&
                <ListGrid list={targetLocations} cellWidth={['100%', '50%', '25%']}>
                  {item => (
                    <AttributionScopedItem itemScope={item?.attribution_scope}>
                      <FlagIcon code={item.iso2} fallback={true} style={{ height: '20px', width: '22px' }} />
                      {item.name}
                    </AttributionScopedItem>
                  )}
                </ListGrid>
              }
              {targetLocations === 'redacted' &&
                <Freemium layout="fullCenter" />
              }
            </Definition>
          </>
        }

      </Details>
    </AttributionScopeProvider>
  )
}

export default CampaignDetails

CampaignDetails.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  fromModal: PropTypes.bool,
}
